import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, skip } from 'rxjs';
import { required } from 'src/app/shared/decorators/required.decorator';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';
import {
  CustomerSegment,
  merchantTypeToCustomerSegment,
} from 'src/app/shared/interfaces/customer-segments';
import { originalOrder } from 'src/app/shared/pipes/keyvalue-pipe-sort';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { notBlank } from 'src/app/shared/validators/not-blank.validator';
import { isPhoneNumber } from 'src/app/shared/validators/phone-number.validator';

import {
  MerchantBasicDataViewModel,
  MerchantSiteMerchantTypeViewModel,
  MerchantSiteViewModel,
  ServedCustomerSegmentResponse,
} from '@app/api/models';
import {
  MerchantActions,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';
import { EasyAddressSubformComponent } from '@shared/components/easy-address-subform/easy-address-subform.component';
import { EasyLocalizedValueSubformComponent } from '@shared/components/easy-localized-value-subform/easy-localized-value-subform.component';
import { EasyPhoneSubformComponent } from '@shared/components/easy-phone-subform/easy-phone-subform.component';
import {
  getServerErrorForField,
  logInvalidControls,
  processErrorResponse,
} from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { MerchantType, merchantTypes } from '@shared/interfaces/merchant-types';
import { isValidEmail } from '@shared/validators/email.validator';
import { merchantTypesValidator } from '@shared/validators/merchant-types.validator.ts';

import { SiteType } from '../../interfaces/merchant-sites';
import { MerchantTypeExciseSubformComponent } from '../merchant-type-excise-subform/merchant-type-excise-subform.component';

@Component({
  selector: 'app-merchant-site-edit',
  templateUrl: './merchant-site-edit.component.html',
  styleUrls: ['./merchant-site-edit.component.scss'],
})
export class MerchantSiteEditComponent
  implements CanComponentDeactivate, OnInit, OnDestroy
{
  TITLE = 'MERCHANT.TITLE.';
  PLACEHOLDER = 'MERCHANT.PLACEHOLDER.';
  HINT = 'MERCHANT.HINT.';
  REQUIRED = this.HINT + 'REQUIRED';

  @Input() @required isLoading!: boolean;
  @Input() @required basicData: MerchantBasicDataViewModel | null | undefined;

  @Input() @required set error(data: ApiErrorResponse | null | undefined) {
    processErrorResponse(this.form, data);
  }

  @Input() @required set site(data: MerchantSiteViewModel | null | undefined) {
    if (data) {
      this.onData(data);
      this.prevMerchantTypes = data.merchantTypes.map(
        (type: MerchantSiteMerchantTypeViewModel) => type.merchantType
      );
      this.prevLicenseNumbers = data.merchantTypes.map(
        (type: MerchantSiteMerchantTypeViewModel) => type.licenseNumber ?? ''
      );
      this.siteId = data.id;
      this.store.dispatch(
        MerchantActions.loadServedCustomerSegments.trigger({
          merchantTypes: this.form.get('merchantTypes')?.value,
        })
      );
    } else {
      this.merchantTypesSubform.initForm(
        [],
        this.form.get('merchantTypes') as UntypedFormArray
      );
    }
  }

  @Output() cancel = new EventEmitter();
  @Output() submitted = new EventEmitter<{
    data: MerchantSiteViewModel;
    isFirstSite: boolean;
  }>();

  @ViewChild(MerchantTypeExciseSubformComponent, { static: true })
  merchantTypesSubform!: MerchantTypeExciseSubformComponent;
  prevMerchantTypes: Array<MerchantType> = [];
  prevLicenseNumbers: Array<string> = [];

  @ViewChild(EasyAddressSubformComponent, { static: true })
  addressSubformComponent!: EasyAddressSubformComponent;

  @ViewChild(EasyPhoneSubformComponent, { static: true })
  phoneSubformComponent!: EasyPhoneSubformComponent;

  readonly merchantTypes = merchantTypes;
  readonly siteTypes = SiteType;
  siteOptions: Array<any> = [];

  subs = new Subscription();

  enabledSegments = false;
  segmentsFirstLoad = true;
  servedSegments: Array<{ name: string; value: string; status: string }> = [];
  autoServedSegments: Array<ServedCustomerSegmentResponse> = [];
  servedSegments$ = this.store.select(
    MerchantSelectors.selectServedCustomerSegments
  );

  siteId?: string;

  showVisibleFields = false;
  showOnSiteServiceFor = false;
  showDeliveryServiceFor = false;

  sitesNumber?: number;

  form = new UntypedFormGroup({
    id: new UntypedFormControl(null), // hidden
    name: new UntypedFormControl('', [Validators.required, notBlank]),
    hasExciseProductsOnSite: new UntypedFormControl(false),
    merchantTypes: new UntypedFormArray([], [merchantTypesValidator]),
    types: new UntypedFormControl(null, Validators.required),
    description: EasyLocalizedValueSubformComponent.buildDefaultForm(),
    address: EasyAddressSubformComponent.buildAddressForm(this.fb),
    phone: new UntypedFormControl('', [Validators.required, isPhoneNumber]),
    email: new UntypedFormControl('', [Validators.required, isValidEmail]),
    openingHours: new UntypedFormGroup({
      monday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      tuesday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      wednesday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      thursday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      friday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      saturday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
      sunday: new UntypedFormGroup({
        from: new UntypedFormControl(),
        to: new UntypedFormControl(),
      }),
    }),
    deliveryServiceAvailable: new UntypedFormControl(false),
    deliveryServiceFor: new UntypedFormControl([]),
    onSiteServiceAvailable: new UntypedFormControl(false),
    onSiteServiceFor: new UntypedFormControl([]),
    unpackagedProductsAvailable: new UntypedFormControl(false),
    visibleFields: this.fb.group({
      name: new UntypedFormControl({ value: true, disabled: true }),
      merchantTypes: new UntypedFormControl({ value: true, disabled: true }),
      types: [false],
      description: [false],
      address: [false],
      phone: [false],
      email: [false],
      openingHours: [false],
      deliveryServiceFor: [false],
      onSiteServiceAvailable: [false],
      onSiteServiceFor: [false],
      unpackagedProductsAvailable: [false],
    }),
  });

  constructor(
    private readonly fb: UntypedFormBuilder,
    readonly router: Router,
    private readonly dialogService: DialogService,
    private store: Store,
    private translateService: TranslateService
  ) {
    this.setSiteOptions();
  }

  ngOnInit(): void {
    this.subs.add(
      this.store
        .select(MerchantSelectors.selectSiteList)
        .subscribe((sites) => (this.sitesNumber = sites?.length ?? 0))
    );

    // TODO ngrxLet-tel feliratkozni
    this.subs.add(
      // skip(1), mert a kezdeti üres lista bezavar
      this.servedSegments$.pipe(skip(1)).subscribe((segments) => {
        this.enabledSegments = !!segments.length;
        const deliveryServiceFor = this.form.get('deliveryServiceFor');
        if (this.showDeliveryServiceFor && this.enabledSegments) {
          deliveryServiceFor?.setValidators(Validators.required);
        }
        const onSiteServiceFor = this.form.get('onSiteServiceFor');
        if (this.showOnSiteServiceFor && this.enabledSegments) {
          onSiteServiceFor?.setValidators(Validators.required);
        }
        // tevékenység típus változás után, pipáljuk be az összes kiszolgálható szegmenst
        if (!this.segmentsFirstLoad) {
          this.form.get('onSiteServiceFor')?.setValue(
            segments.map((item) => item.customerSegment),
            { emitEvent: false }
          );
          this.form.get('deliveryServiceFor')?.setValue(
            segments.map((item) => item.customerSegment),
            { emitEvent: false }
          );
        }
        this.segmentsFirstLoad = false;
        this.autoServedSegments = segments;
        this.servedSegments = segments.map(
          (segment: ServedCustomerSegmentResponse) => ({
            name: this.translateService.instant(
              'MERCHANT.CUSTOMER_SEGMENT.' + segment?.customerSegment
            ),
            value: segment?.customerSegment ?? '',
            status:
              segment?.status === 'APPROVED'
                ? 'ACCEPTED'
                : segment?.status ?? '',
          })
        );
      })
    );
  }

  onData(site: MerchantSiteViewModel) {
    this.showOnSiteServiceFor = site.onSiteServiceAvailable;
    this.showDeliveryServiceFor = site.deliveryServiceAvailable;
    let initialFormValue = this.form.value;
    this.form.reset(initialFormValue);

    this.merchantTypesSubform.initForm(
      site.merchantTypes,
      this.form.get('merchantTypes') as UntypedFormArray
    );

    this.form.patchValue({
      ...site,
      visibleFields: {
        ...site.visibleFields,
        name: true,
        merchantTypes: true,
        deliveryServiceFor:
          (site?.visibleFields && site.visibleFields['deliveryServiceFor']) ??
          false,
      },
    });
    this.form.setControl(
      'description',
      EasyLocalizedValueSubformComponent.buildForm(site.description)
    );

    this.form
      .get('onSiteServiceFor')
      ?.setValue(site.onSiteServiceFor.map((item) => item.customerSegment));
    this.form
      .get('deliveryServiceFor')
      ?.setValue(site.deliveryServiceFor.map((item) => item.customerSegment));

    this.form.markAsPristine();
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.merchantTypesSubform.removeEmptyRows();
      const merchantTypesSubformValue = structuredClone(
        this.merchantTypesSubform.form.value
      );
      merchantTypesSubformValue.forEach(
        (subform: MerchantSiteMerchantTypeViewModel) => {
          if (subform.licenseNumber === '') {
            subform.licenseNumber = undefined;
          }
        }
      );
      const deliveryServiceFor = this.form.value.deliveryServiceFor.map(
        (value: string) => ({ customerSegment: value })
      );
      const onSiteServiceFor = this.form.value.onSiteServiceFor.map(
        (value: string) => ({ customerSegment: value })
      );

      this.submitted.emit({
        data: {
          ...trimInput({
            ...this.form.value,
            phone: this.form.get('phone')?.value,
            email: this.form.get('email')?.value,
            address: this.form.get('address')?.value,
          }),
          merchantTypes: trimInput(merchantTypesSubformValue),
          deliveryServiceFor,
          onSiteServiceFor,
        },
        isFirstSite: this.sitesNumber === 0,
      });
    } else {
      logInvalidControls(this.form);
    }
  }

  onToggleVisibleFields() {
    this.showVisibleFields = !this.showVisibleFields;
  }

  onToggleDeliveryServiceAvailable() {
    if (!this.segmentsFirstLoad) {
      this.showDeliveryServiceFor = this.form.get(
        'deliveryServiceAvailable'
      )?.value;
      const control = this.form.get('deliveryServiceFor');
      if (this.showDeliveryServiceFor && this.enabledSegments) {
        control?.setValidators(Validators.required);
        control?.setValue(
          this.autoServedSegments?.map((item) => item.customerSegment),
          { emitEvent: false }
        );
      } else {
        control?.removeValidators(Validators.required);
        control?.setValue([]);
      }
      control?.updateValueAndValidity();
    }
  }

  onToggleOnSiteServiceAvailable() {
    if (!this.segmentsFirstLoad) {
      this.showOnSiteServiceFor = this.form.get(
        'onSiteServiceAvailable'
      )?.value;
      const control = this.form.get('onSiteServiceFor');
      if (this.showOnSiteServiceFor && this.enabledSegments) {
        control?.setValidators(Validators.required);
        control?.setValue(
          this.autoServedSegments?.map((item) => item.customerSegment),
          { emitEvent: false }
        );
      } else {
        control?.removeValidators(Validators.required);
        control?.setValue([]);
      }
      control?.updateValueAndValidity();
    }
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  setSiteOptions() {
    const selected = (this.form.get('types')?.value as SiteType[]) ?? [];
    const disableHomeDelivery =
      !!selected.length && !selected.includes(SiteType.HOME_DELIVERY_ONLY);
    const disableOthers = selected.includes(SiteType.HOME_DELIVERY_ONLY);

    const siteOptions = Object.values(SiteType).map((opt) => ({
      value: opt,
      name: this.translateService.instant(`MERCHANT.SITE_TYPE.${opt}`),
      disabled:
        opt === SiteType.HOME_DELIVERY_ONLY
          ? disableHomeDelivery
          : disableOthers,
    }));

    this.siteOptions = siteOptions;
  }

  disableIfHomeDeliveryOnly(siteType: SiteType): boolean {
    const selected = (this.form.get('types')?.value as SiteType[]) ?? [];

    // if home delivery is selected, disable everything else
    if (
      selected.includes(SiteType.HOME_DELIVERY_ONLY) &&
      siteType != SiteType.HOME_DELIVERY_ONLY
    ) {
      return true;
    }

    // if anything else is selected, disable home delivery
    if (
      selected.length > 0 &&
      !selected.includes(SiteType.HOME_DELIVERY_ONLY) &&
      siteType == SiteType.HOME_DELIVERY_ONLY
    ) {
      return true;
    }

    return false;
  }

  onMerchantTypeSelectionChange(
    merchantTypes: Array<MerchantSiteMerchantTypeViewModel>
  ) {
    const selectedTypes = merchantTypes.map((t: any) => t.merchantType);
    const licenseNumbers = merchantTypes.map((t) => t.licenseNumber ?? '');
    const merchantTypesChanged = this.merchantTypesChanged(selectedTypes);
    const licenseNumbersChanged = this.licenseNumbersChanged(licenseNumbers);
    if (merchantTypesChanged) {
      this.prevMerchantTypes = selectedTypes;
    }
    if (licenseNumbersChanged) {
      this.prevLicenseNumbers = licenseNumbers;
    }

    if (merchantTypesChanged || licenseNumbersChanged) {
      this.store.dispatch(
        MerchantActions.loadServedCustomerSegments.trigger({
          merchantTypes: this.form.get('merchantTypes')?.value,
        })
      );
    }
  }

  merchantTypesChanged(merchantTypes: Array<MerchantType>): boolean {
    const prevMerchantTypes = this.prevMerchantTypes.sort((a, b) =>
      a > b ? 1 : -1
    );
    const newTypes = merchantTypes.sort((a, b) => (a > b ? 1 : -1));
    return JSON.stringify(newTypes) !== JSON.stringify(prevMerchantTypes);
  }

  licenseNumbersChanged(licenseNumbers: Array<string>): boolean {
    const prevLicenseNumbers = this.prevLicenseNumbers.sort((a, b) =>
      a > b ? 1 : -1
    );
    const newNumbers = licenseNumbers.sort((a, b) => (a > b ? 1 : -1));
    return JSON.stringify(newNumbers) !== JSON.stringify(prevLicenseNumbers);
  }

  getCustomerSegmentsForMerchantTypes(
    types: MerchantType[]
  ): Set<CustomerSegment> {
    const segments = new Set<CustomerSegment>();

    types.forEach((type) => {
      const relatedSegments: CustomerSegment[] =
        merchantTypeToCustomerSegment[type];

      relatedSegments.forEach((newCustomerSegment) => {
        segments.add(newCustomerSegment);
      });
    });

    return segments;
  }

  asArray(subform: AbstractControl): UntypedFormArray {
    return subform as UntypedFormArray;
  }

  useBasicDataPhone(event: MatCheckboxChange) {
    if (event.checked) {
      this.phoneSubformComponent.useBasicDataPhone(this.basicData?.phone);
    } else {
      this.phoneSubformComponent.useBasicDataPhone('');
    }
    this.phoneSubformComponent.disablePhoneInputTrigger(event.checked);
  }

  useBasicDataEmail(event: MatCheckboxChange) {
    if (event.checked) {
      this.form.get('email')?.setValue(this.basicData?.email);
      this.form.get('email')?.disable();
    } else {
      this.form.get('email')?.enable();
      this.form.get('email')?.setValue(null);
    }
  }

  originalOrder = originalOrder;

  get(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }

  canDeactivate() {
    return !this.isLoading && this.form.dirty
      ? this.dialogService.confirmUnsavedChanges()
      : true;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
